import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDiamondsToClaim,
  claimDiamondsFromOfferwalls,
} from '../api/diamonds';
import { createReducer } from '../reducers/factory';
import { loggedSelector } from '../selectors';
import { updateUserData } from '../actions/UserActions';

const name = 'offerwallRewards';
const LOGOUT_SUCCESSFUL = 'LOGOUT_SUCCESSFUL';

const offerwallsRewardsReducer = createReducer('OFFERWALLS_REWARDS', {
  fetchTimeout: 30 * 1000,
  invalidateActions: ['SET_LANGUAGE', 'LOGOUT_SUCCESSFUL', 'LOGIN_SUCCESSFUL'],
  minUpdate: 10 * 60 * 1000,
  initialState: {
    items: [],
  },
});

export const offerwallsRewardsPlainSelector = ({ [name]: offerwallsRewards }) =>
  offerwallsRewards;
export const offerwallsRewardsSelector = ({ [name]: offerwallsRewards }) =>
  offerwallsRewards.items;
export const hasDiamondsToClaimSelector = ({ [name]: offerwallsRewards }) =>
  !!offerwallsRewards.items?.length;

export const fetchOfferwallsRewardsAction =
  () => async (dispatch, getState) => {
    const fetchPromise = (async () => {
      try {
        const state = getState();
        const { apiUrl } = state.auth;
        const { token } = state.auth;

        dispatch(
          offerwallsRewardsReducer.fetchSuccessful(
            await fetchDiamondsToClaim(apiUrl, token),
          ),
        );
      } catch (e) {
        dispatch(offerwallsRewardsReducer.fetchFailed(e));
        throw e;
      }
    })();

    dispatch(offerwallsRewardsReducer.fetchInProgress(fetchPromise));
    return fetchPromise;
  };

export const fetchOfferwallsRewardsIfNeeded =
  () => async (dispatch, getState) => {
    const { fetchPromise, items, ...state } = offerwallsRewardsPlainSelector(
      getState(),
    );
    if (offerwallsRewardsReducer.shouldFetch(state)) {
      return dispatch(fetchOfferwallsRewardsAction());
    }

    return typeof fetchPromise?.then === 'function' ? fetchPromise : items;
  };

export const claimOfferwallsRewards =
  (rewardsIds) => async (dispatch, getState) => {
    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;
    return claimDiamondsFromOfferwalls(apiUrl, token, rewardsIds)
      .then(({ newLootgemBalance }) => {
        dispatch(updateUserData({ lootgemBalance: newLootgemBalance }));
        dispatch(fetchOfferwallsRewardsAction());
        return Promise.resolve(newLootgemBalance);
      })
      .catch((e) => {
        dispatch(fetchOfferwallsRewardsAction());
        return Promise.reject(e);
      });
  };

export const useOfferwallsRewards = () => {
  const data = useSelector(offerwallsRewardsSelector);
  const isLoggedIn = useSelector(loggedSelector);

  const dispatch = useDispatch();
  const { didInvalidate } = data;

  useEffect(() => {
    if (isLoggedIn) dispatch(fetchOfferwallsRewardsIfNeeded()).catch(() => {});
  }, [dispatch, didInvalidate, isLoggedIn]);

  return data;
};

function OfferwallsRewardsReducer(
  state = offerwallsRewardsReducer.INITIAL_STATE,
  action = {},
) {
  switch (action.type) {
    case LOGOUT_SUCCESSFUL:
      return { ...offerwallsRewardsReducer.INITIAL_STATE };
    default:
      return offerwallsRewardsReducer(state, action);
  }
}

export default { [name]: OfferwallsRewardsReducer };
